import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MediaPlayer,
  MediaProvider,
  useMediaRemote,
  useMediaState,
  type MediaPlayerInstance,
  TimeSlider,
  Time,
} from "@vidstack/react";
import { defaultLayoutIcons, DefaultVideoLayout } from "@vidstack/react/player/layouts/default";
import { getCorsSafeUrl } from "@/components/VideoLibrary";
import styled, { css } from "styled-components";
import VideoPlayIcon from "@/assets/svg/videoPlay.svg";
import VideoPauseIcon from "@/assets/svg/videoPause.svg";
import VideoMuteIcon from "@/assets/svg/videoVolumeFull.svg";
import VideoUnmuteIcon from "@/assets/svg/videoVolumeMute.svg";
import ReactPlayer from "react-player";
import { motion, Variants } from "framer-motion";

const VideoPlayerWrapper = styled.div<{ $active: boolean; $hovered: boolean }>`
  width: 100%;
  height: 100%;
  filter: ${({ $active, $hovered }) => ($active || $hovered ? "none" : "grayscale(40%)")};
  transition: ${({ $active, $hovered }) =>
    $active || $hovered ? "filter 0.25s ease-in-out;" : "filter 1s ease-in-out;"};
  position: relative;
  border-radius: 8px;
  overflow: hidden;
`;

const StyledReactPlayer = styled(ReactPlayer)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  aspect-ratio: unset;
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transform-origin: center;
  }
`;

const VideoPlayerControlsWrapper = styled(motion.div)<{ $mobile: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 214749867978687687687687687687687683647110;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ $mobile }) =>
    $mobile &&
    css`
      padding: 16px;
      gap: 8px;
    `}
`;

const SliderRoot = styled(motion.div)`
  width: 100%;
  pointer-events: auto;
  z-index: 2147483647115;
  height: 4px;
`;

const Track = styled.div`
  border-radius: 90px;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.16);
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const TrackFill = styled.div`
  border-top-left-radius: 90px;
  border-bottom-left-radius: 90px;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #d9d9d9;
`;

const PlayerControlsUpperLayer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const PlayerControlsMeta = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const MetaThumbnail = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 100%;
  position: relative;
  overflow: hidden;
  background: #fff;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const MetaLabel = styled.div`
  color: #f7f8f8;
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
`;

const PlayerControlsContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0px;
`;
const PlayerButton = styled.button`
  cursor: pointer;
  width: 32px;
  height: 32px;
  color: #f7f8f8;
  background: none;
  border: none;
  outline: none;
  margin: 0;
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DarkenMedia = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 5;
  user-select: none;
  background:
    linear-gradient(0deg, rgba(31, 33, 33, 0.66) 0%, rgba(31, 33, 33, 0) 62.53%),
    linear-gradient(180deg, rgba(31, 33, 33, 0.3) 0%, rgba(31, 33, 33, 0) 20.29%),
    linear-gradient(0deg, rgba(31, 33, 33, 0.2) 0%, rgba(31, 33, 33, 0.2) 100%);
`;

const ContainerVariants: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.05,
      delayChildren: 0.3,
      staggerDirection: -1,
      delay: 0.4,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function CardVideoPlayer({
  videoItem,
  active,
  handleProgression,
  muted,
  setMuted,
  hovered,
  mobile,
}: {
  videoItem: any;
  active: boolean;
  handleProgression: () => void;
  muted?: boolean;
  setMuted?: (muted: boolean) => void;
  hovered?: boolean;
  mobile?: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const playerRef = useRef<ReactPlayer>(null);
  const [playerProgress, setPlayerProgress] = useState(0);

  useEffect(() => {
    if (active) {
      setPlaying(true);
    } else {
      if (playerRef?.current) {
        setPlaying(false);
        console.log(playerRef?.current);
        playerRef?.current?.seekTo(0);
      }
    }
  }, [active, playerRef]);

  const handleClick = () => {
    if (playing && active) {
      setMuted(true);
    } else if (!playing && active) {
      setMuted(false);
    }
  };

  const handleEnded = () => {
    handleProgression();
  };

  const handlePlaybackProgress = (state) => {
    setPlayerProgress(state.played);
  };

  return (
    <VideoPlayerWrapper $active={active} $hovered={hovered}>
      {mobile && <DarkenMedia />}
      <StyledReactPlayer
        url={getCorsSafeUrl(videoItem?.video?.filename) || ""}
        playing={playing}
        muted={muted}
        onClick={handleClick}
        onEnded={handleEnded}
        onProgress={handlePlaybackProgress}
        ref={playerRef}
        playsinline
        progressInterval={10}
        width='100%'
        height='100%'
        style={{ width: "100%", height: "100%" }}
      />
      <VideoPlayerControlsWrapper
        variants={ContainerVariants}
        initial={mobile ? "visible" : active ? "visible" : "hidden"}
        animate={mobile ? "visible" : active ? "visible" : "hidden"}
        $mobile={mobile}>
        <PlayerControlsUpperLayer>
          <PlayerControlsMeta key={"playerControlsMeta"} variants={itemVariants}>
            <MetaThumbnail>
              <img src={videoItem?.avatar?.filename} alt='thumbnail' />
            </MetaThumbnail>
            <MetaLabel>{videoItem.previewTitle}</MetaLabel>
          </PlayerControlsMeta>
          <PlayerControlsContainer key={"playerControlsContainer"} variants={itemVariants}>
            <PlayButton playing={playing} setPlaying={setPlaying} />
            <MuteButton muted={muted} setMuted={setMuted} />
          </PlayerControlsContainer>
        </PlayerControlsUpperLayer>
        <SliderRoot key={"sliderRoot"} variants={itemVariants}>
          <Track>
            <TrackFill style={{ width: `${playerProgress * 100}%` }} />
          </Track>
        </SliderRoot>
      </VideoPlayerControlsWrapper>
    </VideoPlayerWrapper>
  );
}

const PlayButton = ({ playing, setPlaying }) => {
  return (
    <PlayerButton onClick={() => setPlaying(!playing)}>{playing ? <VideoPauseIcon /> : <VideoPlayIcon />}</PlayerButton>
  );
};

const MuteButton = ({ muted, setMuted }) => {
  return (
    <PlayerButton onClick={() => setMuted(!muted)}>{muted ? <VideoUnmuteIcon /> : <VideoMuteIcon />}</PlayerButton>
  );
};
